/**
 * Product service
 * Handles product fetching, searching, categories, and favorites
 */
import APIClient from '../apiClient';
import axios from 'axios';
import config from '../../config';

export const fetchAllProducts = async (page = 1, limit = 20, sort = "random") => {
  try {
    // Pass page, limit, and sort as query params
    const response = await APIClient.get(`/products/getAllProducts`, {
      params: { page, limit, sort }, // Sending page, limit, and sort as query parameters
    });
    return response.data;
  } catch (error) {
    
    throw error;
  }
};

export const fetchProductDetails = async (pharmacode) => {
  try {
    const response = await APIClient.get(`/products/${pharmacode}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const searchProducts = async (searchTerm, page = 1, limit = 20, categoryFilter = '', sort = 'random', company = '', supplyCategory = '') => {
  try {
    const params = {
      searchTerm: searchTerm,
      page,
      limit,
      sort,
    };

    // Add categoryFilter to params if provided
    if (categoryFilter) {
      params.categoryFilter = categoryFilter;
    }

    // Add company filter if provided
    if (company) {
      params.company = company;
    }

    // Supply (Swissmedic dispensing) category narrows the search rather than
    // replacing it, so it can be combined with the term, category and company.
    if (supplyCategory) {
      params.supplyCategory = supplyCategory;
    }

    const response = await APIClient.get('/products/search', {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Fetch products for a curated symptom key (symptom -> categories on the backend).
export const getProductsBySymptom = async (symptomKey, page = 1, limit = 20, supplyCategory = '') => {
  try {
    const response = await APIClient.get('/products/search', {
      params: { symptom: symptomKey, page, limit, ...(supplyCategory ? { supplyCategory } : {}) },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Fetch products by Swissmedic dispensing category (supply category).
// The shop only ever surfaces 'E' (general sale) and 'D' (pharmacy/druggist
// advice) — see ALLOWED_SUPPLY_CATEGORIES in the backend's
// productInclusionFilter.js — so callers should stick to those two codes.
export const fetchProductsBySupplyCategory = async (code, page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/products/getProductsBySupplyCategory', {
      params: { code, page, limit },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchProductsBySubcategory = async (subcategoryId, page = 1, limit = 20, company = '', sort = '', supplyCategory = '') => {
  try {
    const params = {
      consumerCategoryId: subcategoryId,
      page,
      limit
    };

    // Add company filter if provided
    if (company) {
      params.company = company;
    }

    // Supply (Swissmedic dispensing) category filter — narrows the category
    // listing so the pill works on category and subcategory pages too.
    if (supplyCategory) {
      params.supplyCategory = supplyCategory;
    }

    // Add sort parameter if provided
    if (sort) {
      params.sort = sort;
    }
    
    const response = await APIClient.get(`/products/getProductsByCategory`, {
      params
    });
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch products');
    }
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchCategories = async () => {
  try {
    const response = await APIClient.get(`/products/getCategories`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchProductsByGenericCode = async (genericCode, page = 1, limit = 20) => {
  try {
    const response = await APIClient.get(`/products/getProductsByGenericCodes`, {
      params: { genericCode, page, limit },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchGenericProducts = async (genericCode, level, page = 1, limit = 20, sort = 'random') => {
  try {
    const response = await APIClient.get(`/products/getSimilarProductsByGenericCode`, {
      params: {
        genericCode,
        level,
        page,
        limit,
        sort
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchBestsellers = async (page = 1, limit = 20) => {
  try {
    const response = await APIClient.get('/products/getAllProductsFromBestSeller', {
      params: { page, limit },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Customer-facing discount list.
 *
 * Passes `liveOnly=true` so only discounts whose time window covers "now"
 * come back. Scheduled (future) and expired discounts are hidden from the
 * storefront — they only appear in the admin discount manager.
 *
 * Optional `campaign` filter:
 *   - 'none'  → standalone discounts only (homepage "Our Offers" section)
 *   - '<slug>' → discounts under that specific campaign
 *   - null/undefined → all live discounts (mixed)
 */
export const fetchAllDiscounts = async (page = 1, limit = 20, campaign = null) => {
  try {
    const params = { page, limit, liveOnly: true };
    if (campaign != null) params.campaign = campaign;
    const response = await APIClient.get('/getAllDiscountProduct', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Homepage grouped payload: every live campaign with its products + the
 * standalone products in one round-trip. Sorted by campaign.displayOrder.
 */
export const fetchHomepageDiscounts = async () => {
  try {
    const response = await APIClient.get('/getAllDiscountProduct', {
      params: { groupBy: 'campaign' },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Fetch one campaign by slug — used by the per-campaign listing page. */
export const fetchCampaignBySlug = async (slug) => {
  try {
    const response = await APIClient.get(`/campaigns/${encodeURIComponent(slug)}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const toggleFavoriteProduct = async (pharmacode) => {
  try {
    const url = `/products/favorites/${pharmacode}/toggle`;
    const response = await APIClient.post(url, {});
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchFavorites = async () => {
  try {
    const response = await APIClient.get(`/products/getAllFavorites`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchProductsBySoloField = async (field, value, page = 1, limit = 20) => {
  try {
    // Use direct axios call to bypass the API client interceptor
    const response = await axios.get(`${config.baseURL}/getProductsBySoloField`, {
      params: { field, value, page, limit },
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }
    });
    return response.data;
  } catch (error) {
    
    // Handle specific error cases without triggering automatic redirect
    if (error.response?.status === 401) {
      throw new Error('Authentication required for this feature');
    } else if (error.response?.status === 500) {
      throw new Error('Server error occurred while searching for products');
    } else if (error.response?.status === 400) {
      throw new Error(error.response.data?.error || 'Invalid search parameters');
    } else {
      throw new Error('Failed to search for related products');
    }
  }
};

